import React, { useEffect, useRef, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCardTitle,
  MDBCol,
  MDBIcon,
  MDBInput,
  MDBRow,
  MDBSpinner,
  MDBTypography,
} from "mdb-react-ui-kit";
import { toast } from "react-toastify";

import {
  fetchRorOrganization,
  getGoogleSearchUrl,
  searchRorOrganizations,
} from "../../../helpers/ror";

const ROR_RESULT_LIMIT = 3;
const SEARCH_DEBOUNCE_MS = 400;
const BORDER_COLORS = ["primary", "warning", "info"];

const RorSearchResult = ({ option, onSelectRor, borderColor }) => {
  const location = [option?.city, option?.country].filter(Boolean).join(", ");

  return (
    <MDBCard border={borderColor}>
      <MDBCardBody className="text-start">
        <MDBRow>
          <MDBCol lg={10} md={10}>
            <MDBTypography variant="h5" className="fw-bolder">
              {option.ror_name}
              {location && `, ${location}`}
            </MDBTypography>
            {option.aliases?.length > 0 && (
              <MDBTypography variant="body2" className="text-muted">
                {option.aliases.join(", ")}
              </MDBTypography>
            )}
            <a
              href={option.ror_id}
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none"
            >
              {option.ror_id}
            </a>
          </MDBCol>
          <MDBCol>
            <div
              className="d-flex align-items-center justify-content-end"
              style={{ height: "100%" }}
            >
              <MDBBtn color={borderColor} onClick={() => onSelectRor(option)}>
                Select
              </MDBBtn>
            </div>
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
    </MDBCard>
  );
};

const TaggedAffiliation = ({ affiliation, taggedSubstrings }) => {
  const textClassName = "fw-bolder lh-base";

  if (!taggedSubstrings.length) {
    return <span className={textClassName}>{affiliation}</span>;
  }

  const parts = [];
  let remaining = affiliation;

  const sortedTags = [...taggedSubstrings].sort(
    (a, b) => affiliation.indexOf(a) - affiliation.indexOf(b)
  );

  sortedTags.forEach((tag) => {
    const index = remaining.indexOf(tag);
    if (index === -1) {
      return;
    }

    if (index > 0) {
      parts.push({ text: remaining.slice(0, index), tagged: false });
    }

    parts.push({ text: tag, tagged: true });
    remaining = remaining.slice(index + tag.length);
  });

  if (remaining) {
    parts.push({ text: remaining, tagged: false });
  }

  return (
    <span className={textClassName}>
      {parts.map((part, index) =>
        part.tagged ? (
          <mark key={`${part.text}-${index}`} className="bg-warning px-1">
            {part.text}
          </mark>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        )
      )}
    </span>
  );
};

export const TagRorCard = ({ task, onTagRor }) => {
  const affiliationRef = useRef(null);
  const affiliation = task.data?.affiliation || "";

  const [taggedSubstrings, setTaggedSubstrings] = useState([]);
  const [searchQuery, setSearchQuery] = useState(affiliation);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [customRorId, setCustomRorId] = useState("");
  const [customRorResult, setCustomRorResult] = useState(null);
  const [isLookingUpCustomRor, setIsLookingUpCustomRor] = useState(false);

  useEffect(() => {
    setTaggedSubstrings([]);
    setSearchQuery(affiliation);
    setSearchResults([]);
    setHasSearched(false);
    setCustomRorId("");
    setCustomRorResult(null);
    setIsLookingUpCustomRor(false);
  }, [task.id, affiliation]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchRorOrganizations(query, ROR_RESULT_LIMIT);
        setSearchResults(results);
        setHasSearched(true);
      } catch (error) {
        toast("Error searching ROR records", { type: "error" });
        setSearchResults([]);
        setHasSearched(true);
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const buildOutput = (selection, rorMissing) => {
    const output = {
      search_query: searchQuery.trim() || affiliation,
      ror_id: selection?.ror_id ?? null,
    };

    if (selection?.ror_name) {
      output.ror_name = selection.ror_name;
    }

    if (taggedSubstrings.length > 0) {
      output.tagged_substrings = taggedSubstrings;
    }

    if (output.ror_id === null && rorMissing) {
      output.ror_missing = rorMissing;
    }

    return output;
  };

  const handleTagSelection = () => {
    const selection = window.getSelection();
    const container = affiliationRef.current;

    if (
      !selection ||
      selection.isCollapsed ||
      !container ||
      !container.contains(selection.anchorNode) ||
      !container.contains(selection.focusNode)
    ) {
      toast("Select text in the affiliation box first, then click Tag selected text", {
        type: "warning",
      });
      return;
    }

    const selectedText = selection.toString().trim();

    if (!selectedText) {
      toast("Select text in the affiliation box first, then click Tag selected text", {
        type: "warning",
      });
      return;
    }

    if (!affiliation.includes(selectedText)) {
      toast("Selected text must be part of the affiliation", { type: "error" });
      return;
    }

    if (taggedSubstrings.includes(selectedText)) {
      toast("This substring is already tagged", { type: "info" });
      return;
    }

    setTaggedSubstrings((current) => [...current, selectedText]);
    selection.removeAllRanges();
  };

  const handleRemoveTag = (tag) => {
    setTaggedSubstrings((current) => current.filter((item) => item !== tag));
  };

  const handleGoogleSearch = () => {
    const query = searchQuery.trim() || affiliation;
    if (!query) {
      toast("Search query cannot be empty", { type: "error" });
      return;
    }

    window.open(getGoogleSearchUrl(query), "_blank", "noopener,noreferrer");
  };

  const handleLookupCustomRor = async () => {
    if (!customRorId.trim()) {
      toast("Enter a ROR ID to look up", { type: "warning" });
      return;
    }

    setIsLookingUpCustomRor(true);
    setCustomRorResult(null);

    try {
      const result = await fetchRorOrganization(customRorId);
      setCustomRorResult(result);
      setCustomRorId(result.ror_id);
    } catch (error) {
      toast("Could not find a ROR record for that ID", { type: "error" });
    } finally {
      setIsLookingUpCustomRor(false);
    }
  };

  const handleSelectRor = (option) => {
    onTagRor(
      buildOutput({
        ror_id: option.ror_id,
        ror_name: option.ror_name,
      })
    );
  };

  const handleNoMatch = (rorMissing) => {
    onTagRor(buildOutput(null, rorMissing));
  };

  return (
    <MDBCard className="mb-5">
      <MDBCardHeader>
        <MDBCardTitle className="lead mt-1">
          Tag affiliation and find matching ROR record
        </MDBCardTitle>
      </MDBCardHeader>
      <MDBCardBody>
        <div className="text-start mb-4">
          <MDBTypography variant="h6" className="mb-2">
            1. Optional — tag organization names
          </MDBTypography>
          <MDBTypography variant="body2" className="text-muted mb-3">
            Use the <strong>affiliation text box</strong> below only. Highlight part of the text with your mouse,
            then click <strong>Tag selected text</strong>. Do <strong>not</strong> select text in the search field in step 2.
          </MDBTypography>

          <label className="form-label mb-1" htmlFor="tag-ror-affiliation">
            Affiliation text — select here to tag
          </label>
          <div
            id="tag-ror-affiliation"
            ref={affiliationRef}
            className="form-control py-3"
            style={{
              height: "auto",
              cursor: "text",
              whiteSpace: "normal",
              userSelect: "text",
            }}
          >
            <TaggedAffiliation
              affiliation={affiliation}
              taggedSubstrings={taggedSubstrings}
            />
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
            <MDBBtn size="sm" color="secondary" onClick={handleTagSelection}>
              Tag selected text
            </MDBBtn>
          </div>

          {taggedSubstrings.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <MDBTypography variant="body2" className="text-muted mb-0 me-1">
                Tagged:
              </MDBTypography>
              {taggedSubstrings.map((tag) => (
                <MDBBadge
                  key={tag}
                  color="warning"
                  className="d-inline-flex align-items-center"
                  pill
                >
                  {tag}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    aria-label={`Remove tag ${tag}`}
                    onClick={() => handleRemoveTag(tag)}
                    style={{ fontSize: "0.55rem" }}
                  />
                </MDBBadge>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4">
          <MDBTypography variant="h6" className="mb-2">
            2. Search for the ROR record
          </MDBTypography>
          <MDBTypography variant="body2" className="text-muted mb-3">
            Edit the <strong>search query</strong> below to find the right organization in ROR.
            This field is for typing your search — not for tagging.
          </MDBTypography>
          <MDBRow className="g-2 align-items-start">
            <MDBCol md={9}>
              <textarea
                id="tag-ror-search-query"
                className="form-control"
                rows={3}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </MDBCol>
            <MDBCol md={3} className="pt-4">
              <MDBBtn
                color="light"
                className="w-100"
                onClick={handleGoogleSearch}
              >
                <MDBIcon fab icon="google" className="me-2" />
                Search Google
              </MDBBtn>
            </MDBCol>
          </MDBRow>
          <MDBTypography variant="body2" className="text-muted mt-2 mb-0">
            Shorten the query if needed (e.g. just the university name). ROR results update automatically as you type.
          </MDBTypography>
        </div>

        {isSearching && (
          <div className="text-center mb-4">
            <MDBSpinner />
            <MDBTypography variant="body2" className="text-muted mt-2 mb-0">
              Searching ROR...
            </MDBTypography>
          </div>
        )}

        {hasSearched && !isSearching && (
          <>
            <MDBTypography variant="h6" className="mb-3">
              3. Select a result
            </MDBTypography>
            <MDBRow className="g-3">
            {searchResults.length === 0 && (
              <MDBTypography variant="body1" className="text-muted text-center mb-0">
                No ROR records found for this query.
              </MDBTypography>
            )}
            {searchResults.map((option, index) => (
              <RorSearchResult
                key={option.ror_id}
                option={option}
                onSelectRor={handleSelectRor}
                borderColor={BORDER_COLORS[index % BORDER_COLORS.length]}
              />
            ))}
            <MDBCard border="dark">
              <MDBCardBody className="text-start">
                <MDBTypography variant="h5" className="fw-bolder mb-2">
                  Custom ROR ID
                </MDBTypography>
                <MDBTypography variant="body2" className="text-muted mb-3">
                  Paste a ROR ID if the correct record is not in the top 3 results above
                  (e.g. <em>https://ror.org/00qyh5r35</em>).
                </MDBTypography>
                <MDBRow className="g-2 align-items-end">
                  <MDBCol md={9}>
                    <MDBInput
                      label="ROR ID"
                      value={customRorId}
                      onChange={(event) => {
                        setCustomRorId(event.target.value);
                        setCustomRorResult(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleLookupCustomRor();
                        }
                      }}
                    />
                  </MDBCol>
                  <MDBCol md={3}>
                    <MDBBtn
                      color="dark"
                      className="w-100"
                      onClick={handleLookupCustomRor}
                      disabled={isLookingUpCustomRor}
                    >
                      {isLookingUpCustomRor ? (
                        <MDBSpinner size="sm" />
                      ) : (
                        "Look up"
                      )}
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
                {customRorResult && (
                  <div className="mt-3">
                    <RorSearchResult
                      option={customRorResult}
                      onSelectRor={handleSelectRor}
                      borderColor="success"
                    />
                  </div>
                )}
              </MDBCardBody>
            </MDBCard>
            <MDBCard border="danger">
              <MDBCardBody className="text-start">
                <MDBRow>
                  <MDBCol md={9}>
                    <MDBTypography variant="h5" className="fw-bolder">
                      Invalid affiliation
                    </MDBTypography>
                    <MDBTypography variant="body2" className="text-muted">
                      It is unlikely we can find a ROR record for this affiliation.
                    </MDBTypography>
                  </MDBCol>
                  <MDBCol>
                    <div
                      className="d-flex align-items-center justify-content-end"
                      style={{ height: "100%" }}
                    >
                      <MDBBtn
                        color="danger"
                        onClick={() => handleNoMatch("invalid")}
                      >
                        Select
                      </MDBBtn>
                    </div>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
            <MDBCard border="secondary">
              <MDBCardBody className="text-start">
                <MDBRow>
                  <MDBCol md={9}>
                    <MDBTypography variant="h5" className="fw-bolder">
                      Not in ROR
                    </MDBTypography>
                    <MDBTypography variant="body2" className="text-muted">
                      This affiliation is not in the ROR register yet.
                    </MDBTypography>
                  </MDBCol>
                  <MDBCol>
                    <div
                      className="d-flex align-items-center justify-content-end"
                      style={{ height: "100%" }}
                    >
                      <MDBBtn
                        color="secondary"
                        onClick={() => handleNoMatch("notinror")}
                      >
                        Select
                      </MDBBtn>
                    </div>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBRow>
          </>
        )}
      </MDBCardBody>
    </MDBCard>
  );
};
